import React, { useState } from 'react';
import StepLayout from '../components/StepLayout';
import useEnterKey from '../hooks/useEnterKey';
import { api } from '../api';

// iter-26: rebuild per Referrer Name Page UI Spec.
//   Heading       — "Who referred you? *"
//   Subtitle      — "Enter their full name so we can credit them when
//                    your application is approved."
//   Field         — "Full name *" label, e.g. "Sarah Johnson" placeholder,
//                    underline 1px #888 → focus #F26C1F
//   Disabled Next — until 2+ chars with at least one space (basic
//                    "first + last name" gate)
//   ⌘+Enter hint  — desktop only; hidden on touch via @media (hover: none)
//   Trustpilot    — kept (StepLayout default)
//
// App-level skip rule already handles: only render when previous step
// chose Referral (not Facebook ad).
export default function Step16ReferrerName({ sessionId, sessionData, updateData, onNext }) {
  const [name, setName] = useState(sessionData.referrerName || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const trimmed = name.trim();
  const hasFullName = trimmed.length >= 2 && /\s/.test(trimmed);
  const canSubmit = hasFullName && !loading;

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!canSubmit) {
      setError('Please enter the referrer’s full name (first and last).');
      return;
    }
    setLoading(true);
    setSubmitError('');
    try {
      await api.updateReferral(sessionId, { referrerName: trimmed });
      updateData({ referrerName: trimmed });
      onNext();
    } catch (err) {
      setSubmitError(err.message || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  // ⌘/Ctrl + Enter to submit per spec. Plain Enter inside the field is
  // not enough — operator wants the deliberate two-key gesture.
  useEnterKey(() => handleSubmit({ preventDefault: () => {} }), { enabled: canSubmit, requireMeta: true });

  return (
    <StepLayout title="Who referred you? *">
      <p style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 16,
        lineHeight: 1.55,
        color: '#444',
        margin: '0 0 32px',
      }}>
        Enter their full name so we can credit them when your application is approved.
      </p>

      <form onSubmit={handleSubmit} className="referrer-form">
        <div className="form-field" style={{ maxWidth: 480 }}>
          <label htmlFor="referrerName" className="form-field__label">
            Full name <span className="form-field__req">*</span>
          </label>
          <input
            id="referrerName"
            type="text"
            placeholder="e.g. Sarah Johnson"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
            className="form-field__input referrer-input"
            autoComplete="off"
            aria-required="true"
          />
          {error && <p className="form-field__error">{error}</p>}
        </div>

        {submitError && <p className="form-field__error" style={{ marginTop: 12 }}>{submitError}</p>}

        <div className="cta-row" style={{ marginTop: 32 }}>
          <button
            type="submit"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            className="cta-row__btn primary-cta"
          >
            {loading ? 'Saving...' : 'Next'}
          </button>
          <span className="enter-hint enter-hint--meta">press <strong>⌘ + Enter ↵</strong></span>
        </div>
      </form>
    </StepLayout>
  );
}
